import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import {
  ArrowRightLeftIcon,
  ArrowUpDownIcon,
  Loader2,
  MoveDownIcon,
  MoveRightIcon,
} from "lucide-react"
import { useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { z } from "zod"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { InputMoney } from "@/components/ui/input-money"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { FRONTEND_ROUTES } from "@/constants"
import { formatMoney } from "@/helpers/format"
import { useSmartRoutingConfiguration } from "@/hooks/use-smart-routing-configuration"
import {
  useCreateSmartRoutingConfiguration,
  useUpdateSmartRoutingConfiguration,
} from "@/hooks/use-smart-routing-configuration-mutation"
import { useSmartRoutingPaymentMethods } from "@/hooks/use-smart-routing-payment-methods"
import { useSmartRoutingPeriods } from "@/hooks/use-smart-routing-periods"
import { useSmartRoutingProcessors } from "@/hooks/use-smart-routing-processors"
import { useWhiteLabels } from "@/hooks/use-white-labels"

const parseMoney = (val: unknown): number => {
  if (typeof val === "number") return val
  if (typeof val === "string") {
    const cleaned = val
      .replace(/[R$\s]/g, "")
      .replace(/\./g, "")
      .replace(",", ".")
    const num = Number(cleaned)
    return isNaN(num) ? 0 : Math.round(num * 100)
  }
  return 0
}

// Formata centavos para string de moeda (800000 → "R$ 8.000,00")
const formatCentsToMoney = (cents: number): string => {
  return formatMoney(cents / 100)
}

const moneyField = z
  .preprocess(parseMoney, z.number())
  .pipe(
    z.number().min(0, "Meta deve ser maior ou igual a 0")
  )

const moneyFieldOptional = z
  .preprocess(parseMoney, z.number())
  .pipe(z.number().min(0).optional())

const formSchema = z.object({
  status: z.enum(["ACTIVE", "INACTIVE"]).default("ACTIVE"),
  whiteLabelId: z
    .string()
    .min(1, "White Label é obrigatório"),
  paymentMethod: z
    .string()
    .min(1, "Método de pagamento é obrigatório"),
  period: z.enum(["DAILY", "WEEKLY", "MONTHLY", "YEARLY"]),
  firstProcessor: z.object({
    processorId: z
      .string()
      .min(1, "Gateway primário é obrigatório"),
    target: moneyField,
    routingByTarget: z.boolean().default(false),
  }),
  secondProcessor: z.object({
    processorId: z.string().optional(),
    target: moneyFieldOptional,
    routingByTarget: z.boolean().default(false),
  }),
})

type FormValues = z.infer<typeof formSchema>

export function SmartRoutingConfigurationFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const isEditing = Boolean(uuid)

  const { data: configurationData, isLoading } =
    useSmartRoutingConfiguration(uuid)
  const {
    data: periods = [],
    isLoading: isLoadingPeriods,
  } = useSmartRoutingPeriods()
  const {
    data: paymentMethods = [],
    isLoading: isLoadingPaymentMethods,
  } = useSmartRoutingPaymentMethods()
  const {
    data: whiteLabels = [],
    isLoading: isLoadingWhiteLabels,
  } = useWhiteLabels()
  const createMutation =
    useCreateSmartRoutingConfiguration()
  const updateMutation = useUpdateSmartRoutingConfiguration(
    uuid || ""
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      status: "ACTIVE",
      whiteLabelId: "",
      paymentMethod: "",
      period: "MONTHLY",
      firstProcessor: {
        processorId: "",
        target: 0,
        routingByTarget: false,
      },
      secondProcessor: {
        processorId: "",
        target: 0,
        routingByTarget: false,
      },
    },
  })

  // useWatch no lugar de form.watch(): este devolve funcao nova a cada render e
  // faz o React Compiler desistir de memoizar a pagina inteira.
  const paymentMethod = useWatch({
    control: form.control,
    name: "paymentMethod",
  })
  const routingByTarget = useWatch({
    control: form.control,
    name: "secondProcessor.routingByTarget",
  })
  const {
    data: processors = [],
    isLoading: isLoadingProcessors,
  } = useSmartRoutingProcessors(
    paymentMethod || configurationData?.paymentMethod
  )

  const isLoadingForm =
    isLoading ||
    isLoadingPeriods ||
    isLoadingPaymentMethods ||
    isLoadingWhiteLabels ||
    (isEditing && isLoadingProcessors)

  useEffect(() => {
    if (configurationData && processors.length > 0) {
      form.reset({
        status: configurationData.status,
        whiteLabelId: configurationData.whiteLabelId,
        paymentMethod: configurationData.paymentMethod,
        period: configurationData.period,
        firstProcessor: {
          processorId:
            configurationData.firstProcessor.processor.id,
          target: formatCentsToMoney(
            configurationData.firstProcessor.target
          ) as unknown as number,
          routingByTarget:
            configurationData.firstProcessor
              .routingByTarget,
        },
        secondProcessor: configurationData.secondProcessor
          ? {
              processorId:
                configurationData.secondProcessor.processor
                  .id,
              target: formatCentsToMoney(
                configurationData.secondProcessor.target
              ) as unknown as number,
              routingByTarget:
                configurationData.secondProcessor
                  .routingByTarget,
            }
          : {
              processorId: "",
              target: 0,
              routingByTarget: false,
            },
      })
    }
  }, [configurationData, processors, form])

  const buildProcessorPayload = (
    processorConfigId: string
  ) => {
    const proc = processors.find(
      (p) => p.id === processorConfigId
    )
    if (!proc) return null
    return {
      processor: { id: proc.processor.id },
      processorId: proc.processorId,
    }
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const firstProc = buildProcessorPayload(
        values.firstProcessor.processorId
      )
      if (!firstProc) return

      const secondProc = values.secondProcessor?.processorId
        ? buildProcessorPayload(
            values.secondProcessor.processorId
          )
        : undefined

      const payload = {
        ...(isEditing && uuid ? { id: uuid } : {}),
        whiteLabelId: values.whiteLabelId,
        paymentMethod: values.paymentMethod,
        period: values.period,
        status: values.status,
        firstProcessor: {
          ...firstProc,
          target: values.firstProcessor.target,
          routingByTarget:
            values.firstProcessor.routingByTarget,
        },
        ...(secondProc && values.secondProcessor
          ? {
              secondProcessor: {
                ...secondProc,
                target: values.secondProcessor.target ?? 0,
                routingByTarget:
                  values.secondProcessor.routingByTarget,
              },
            }
          : {}),
      }

      if (isEditing) {
        await updateMutation.mutateAsync(payload)
      } else {
        await createMutation.mutateAsync(payload)
      }
      navigate(
        FRONTEND_ROUTES.SMART_ROUTING_CONFIGURATIONS.href
      )
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  const handleCancel = () => {
    navigate(
      FRONTEND_ROUTES.SMART_ROUTING_CONFIGURATIONS.href
    )
  }

  if (isEditing && isLoadingForm) {
    return (
      <DashboardContainer>
        <Header>
          <Skeleton className="h-8 w-64" />
        </Header>
        <section className="flex flex-1 flex-col gap-4 p-4">
          <Card>
            <CardContent className="space-y-6 pt-6">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-6 w-12" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>

              <div className="space-y-4 rounded-lg border p-4">
                <Skeleton className="h-6 w-48" />
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              </div>

              <div className="space-y-4 rounded-lg border p-4">
                <Skeleton className="h-6 w-56" />
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.SMART_ROUTING.href,
              label: t("sidebar.smartRouting"),
            },
            {
              label: isEditing
                ? t("pages.smartRouting.form.editTitle")
                : t("pages.smartRouting.form.createTitle"),
            },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="text-2xl font-bold">
          {isEditing
            ? t("pages.smartRouting.form.editTitle")
            : t("pages.smartRouting.form.createTitle")}
        </h1>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <Tabs defaultValue="configuration">
              <TabsList className="mb-4">
                <TabsTrigger value="configuration">
                  {t(
                    "pages.smartRouting.form.tabs.configuration"
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="configuration">
                <Card>
                  <CardContent className="space-y-6 pt-6">
                    {/* White Label, Método de Pagamento e Período */}
                    <div className="grid gap-4 md:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="whiteLabelId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t(
                                "pages.smartRouting.form.whiteLabel"
                              )}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "common.selectOption"
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {whiteLabels.map(
                                  (whiteLabel) => (
                                    <SelectItem
                                      key={whiteLabel.uuid}
                                      value={
                                        whiteLabel.uuid
                                      }
                                    >
                                      {whiteLabel.name}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="paymentMethod"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t(
                                "pages.smartRouting.form.paymentMethod"
                              )}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "common.selectOption"
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {paymentMethods.map(
                                  (method) => (
                                    <SelectItem
                                      key={method.type}
                                      value={method.type}
                                    >
                                      {method.description}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="period"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t(
                                "pages.smartRouting.form.period"
                              )}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {periods.map((period) => (
                                  <SelectItem
                                    key={period.type}
                                    value={period.type}
                                  >
                                    {period.description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Configurações Primária e Secundária */}
                    <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr]">
                      {/* Configuração Primária */}
                      <div className="space-y-4 rounded-lg border p-4">
                        <h3 className="font-semibold">
                          {t(
                            "pages.smartRouting.form.primaryConfiguration"
                          )}
                        </h3>

                        <div className="grid gap-4">
                          <FormField
                            control={form.control}
                            name="firstProcessor.processorId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "pages.smartRouting.form.gateway"
                                  )}
                                </FormLabel>
                                <Select
                                  onValueChange={
                                    field.onChange
                                  }
                                  value={field.value}
                                  disabled={
                                    processors.length === 0
                                  }
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={
                                          processors.length ===
                                          0
                                            ? t(
                                                "common.noRecordsFound"
                                              )
                                            : t(
                                                "common.selectOption"
                                              )
                                        }
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {processors.map(
                                      (proc) => (
                                        <SelectItem
                                          key={proc.id}
                                          value={proc.id}
                                        >
                                          {
                                            proc.processor
                                              .name
                                          }{" "}
                                          (
                                          {proc.processorId}
                                          )
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="firstProcessor.target"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "pages.smartRouting.form.target"
                                  )}
                                </FormLabel>
                                <FormControl>
                                  <InputMoney {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="firstProcessor.routingByTarget"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center gap-2 rounded-lg border p-3">
                              <FormLabel className="text-sm">
                                {t(
                                  "pages.smartRouting.form.routingByVolume"
                                )}
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={
                                    field.onChange
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Indicador de Fluxo */}
                      <div className="flex flex-col items-center justify-center">
                        <div className="rounded-full bg-primary/10 p-4">
                          {routingByTarget ? (
                            <>
                              <ArrowUpDownIcon className="h-10 w-10 text-primary lg:hidden" />
                              <ArrowRightLeftIcon className="hidden h-10 w-10 text-primary lg:block" />
                            </>
                          ) : (
                            <>
                              <MoveDownIcon className="h-10 w-10 text-primary lg:hidden" />
                              <MoveRightIcon className="hidden h-10 w-10 text-primary lg:block" />
                            </>
                          )}
                        </div>
                        <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Failover
                        </span>
                      </div>

                      {/* Configuração Secundária */}
                      <div className="space-y-4 rounded-lg border p-4">
                        <h3 className="font-semibold">
                          {t(
                            "pages.smartRouting.form.secondaryConfiguration"
                          )}
                        </h3>

                        <div className="grid gap-4">
                          <FormField
                            control={form.control}
                            name="secondProcessor.processorId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "pages.smartRouting.form.gateway"
                                  )}
                                </FormLabel>
                                <Select
                                  onValueChange={
                                    field.onChange
                                  }
                                  value={field.value}
                                  disabled={
                                    processors.length === 0
                                  }
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue
                                        placeholder={
                                          processors.length ===
                                          0
                                            ? t(
                                                "common.noRecordsFound"
                                              )
                                            : t(
                                                "common.selectOption"
                                              )
                                        }
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {processors.map(
                                      (proc) => (
                                        <SelectItem
                                          key={proc.id}
                                          value={proc.id}
                                        >
                                          {
                                            proc.processor
                                              .name
                                          }{" "}
                                          (
                                          {proc.processorId}
                                          )
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="secondProcessor.target"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "pages.smartRouting.form.target"
                                  )}
                                </FormLabel>
                                <FormControl>
                                  <InputMoney {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="secondProcessor.routingByTarget"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center gap-2 rounded-lg border p-3">
                              <FormLabel className="text-sm">
                                {t(
                                  "pages.smartRouting.form.automaticRoutingAfterTarget"
                                )}
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={
                                    field.onChange
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Status Ativo */}
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2 rounded-lg border p-4">
                          <FormLabel className="text-base font-semibold">
                            {t(
                              "pages.smartRouting.form.activeConfiguration"
                            )}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={
                                field.value === "ACTIVE"
                              }
                              onCheckedChange={(checked) =>
                                field.onChange(
                                  checked
                                    ? "ACTIVE"
                                    : "INACTIVE"
                                )
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {/* Timestamps (apenas para edição) */}
                    {isEditing && configurationData && (
                      <div className="grid gap-4 rounded-lg border bg-muted/50 p-4 text-sm md:grid-cols-2">
                        <div>
                          <span className="font-medium">
                            {t(
                              "pages.smartRouting.form.createdAt"
                            )}
                            :
                          </span>{" "}
                          <span className="text-muted-foreground">
                            {format(
                              new Date(
                                configurationData.createdAt
                              ),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">
                            {t(
                              "pages.smartRouting.form.updatedAt"
                            )}
                            :
                          </span>{" "}
                          <span className="text-muted-foreground">
                            {format(
                              new Date(
                                configurationData.updatedAt
                              ),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending
                }
              >
                {(createMutation.isPending ||
                  updateMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("common.save")}
              </Button>
            </div>
          </form>
        </Form>
      </section>
    </DashboardContainer>
  )
}
